import { useCallback, useEffect, useState } from 'react'
import { Linking } from 'react-native'
import PushNotification from 'react-native-push-notification'
import { RESULTS } from 'react-native-permissions'

import { isIOS, isAndroid } from 'APP/Helpers/checkPlatform'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import {
  checkPostNotificationsState,
  requestPostNotificationsPermissions,
} from 'APP/NativeModules/PermissionsService'
import { logger } from 'APP/Lib/Datadog'

interface UsePushNotificationPermissionOptions {
  onPermissionsChanged?: () => void
  source?: string
}

export const usePushNotificationPermission = ({
  onPermissionsChanged,
  source,
}: UsePushNotificationPermissionOptions = {}) => {
  const [permissionStatus, setPermissionStatus] = useState<string | undefined>(undefined)

  // We can only trigger the OS prompt when the user has never been asked. Outside the embedded
  // shell users always come through PushNotificationPermissionScreen first, so RESULTS.DENIED is
  // not expected; only inside the host app can the user reach this screen without ever being
  // prompted at the OS level (e.g. they tapped "Maybe Later" on the host app's permission screen).
  const canPromptForPermission = isEmbeddedEnvironment() && permissionStatus === RESULTS.DENIED

  const refreshStatus = useCallback(async () => {
    try {
      const next = await checkPostNotificationsState()
      setPermissionStatus(next)
    } catch (e) {
      logger.error('Push notification permission status check failed', { source }, e)
    }
  }, [source])

  useEffect(() => {
    refreshStatus()
  }, [refreshStatus])

  const requestPermissionOrOpenSettings = useCallback(async () => {
    if (!canPromptForPermission) {
      Linking.openSettings()
      return
    }

    try {
      if (isIOS()) {
        await PushNotification.requestPermissions()
      } else if (isAndroid()) {
        await requestPostNotificationsPermissions()
      }
    } catch (e) {
      logger.error('Push notification permission request failed', { source }, e)
      Linking.openSettings()
      return
    }

    // Refresh local status so the button label reflects the new state. If the user denied the
    // prompt, status flips to BLOCKED and the next tap will go to device settings.
    await refreshStatus()
    onPermissionsChanged?.()
  }, [canPromptForPermission, onPermissionsChanged, refreshStatus, source])

  return {
    permissionStatus,
    canPromptForPermission,
    refreshStatus,
    requestPermissionOrOpenSettings,
  }
}
